/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Verdi Dashboard API
 * API behind the Verdi irrigation dashboard: organizations, their devices, zones and irrigation schedules, and the sensor data those devices report.
 *
 * ## Authentication
 *
 * Most operations need a Descope session token, sent as `Authorization: Bearer <token>`. Each operation's `security` lists what it accepts. Role and permission names there, such as `Verdi Administrator`, are Descope roles or permissions the session must carry, and the operation's description repeats them under **Access**. Operations with `security: []` are public.
 *
 * ## Organization context
 *
 * Data belongs to organizations. A user's session acts as their primary organization, or as another organization they belong to when its ID is sent in the `User` header. Administrators must send the `User` header on organization-scoped operations, or the request is rejected with a 400.
 *
 * ## Errors
 *
 * Error bodies are JSON `{ "message": string }`.
 *
 * - **400**: the path, query or body failed validation, or the request broke a domain rule.
 * - **401 / 403**: sent by the auth middleware; the body may be empty.
 * - **409**: see below.
 * - **500**: an unexpected server error.
 *
 * ## Versioned resources
 *
 * Versioned resources carry a `versionId`. Send the `versionId` you last read on a PATCH (in the body) or a DELETE (as a query parameter). If the resource changed since, the request gets a 409: reload it and retry. A 409 that includes a `code` is a conflict specific to that operation, described on the operation. On a create, 409 means the resource already exists.
 *
 * ## Reads with a request body
 *
 * Some reads take their input as a JSON body on the HTTP `QUERY` method; these operations are marked `x-read-only: true`. Deprecated POST and GET variants serve clients that cannot send `QUERY`.
 *
 * ## Identifiers
 *
 * MongoDB ObjectIds are 24-character hexadecimal strings.
 * OpenAPI spec version: 1.0.0
 */

import type {
	DataTag,
	DefinedInitialDataOptions,
	DefinedUseQueryResult,
	QueryClient,
	QueryFunction,
	QueryKey,
	UndefinedInitialDataOptions,
	UseQueryOptions,
	UseQueryResult,
} from "@tanstack/react-query";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "../custom-fetch.ts";
import type {
	GetRecentSessionData400,
	GetRecentSessionData500,
	LoadSessionData200,
	LoadSessionData400,
	LoadSessionData403,
	LoadSessionData500,
	LoadSessionDataParams,
	PollForUpdates200,
	PollForUpdates400,
	PollForUpdates401,
	PollForUpdates500,
	PollForUpdatesBody,
	RecentDataItem,
} from "./schemas";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(
	query: T,
	queryKey: K,
): T & { queryKey: K } => {
	const result = { queryKey } as T & { queryKey: K };
	for (const key of Object.keys(query)) {
		// The explicit queryKey always wins, matching the previous
		// `{ ...query, queryKey }` spread where it was set last.
		if (key === "queryKey") continue;
		Object.defineProperty(result, key, {
			enumerable: true,
			configurable: true,
			get: () => (query as Record<string, unknown>)[key],
		});
	}
	return result;
};

export const getGetRecentSessionDataUrl = () => {
	return `/session/recent-data`;
};

/**
 * **Access:** Requires a user session token.
 * @summary Get the most recent sensor data for the current user's devices
 */
export const getRecentSessionData = async (
	options?: Parameters<typeof customFetch>[1],
): Promise<RecentDataItem[]> => {
	return customFetch<RecentDataItem[]>(getGetRecentSessionDataUrl(), {
		...options,
		method: "GET",
	});
};

export const getGetRecentSessionDataQueryKey = () => {
	return ["session", "recent-data"] as const;
};

export const getGetRecentSessionDataQueryOptions = <
	TData = Awaited<ReturnType<typeof getRecentSessionData>>,
	TError = GetRecentSessionData400 | void | GetRecentSessionData500,
>(options?: {
	query?: Partial<
		UseQueryOptions<
			Awaited<ReturnType<typeof getRecentSessionData>>,
			TError,
			TData
		>
	>;
	request?: SecondParameter<typeof customFetch>;
}) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey = queryOptions?.queryKey ?? getGetRecentSessionDataQueryKey();

	const queryFn: QueryFunction<
		Awaited<ReturnType<typeof getRecentSessionData>>
	> = ({ signal }) => getRecentSessionData({ signal, ...requestOptions });

	return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
		Awaited<ReturnType<typeof getRecentSessionData>>,
		TError,
		TData
	> & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetRecentSessionDataQueryResult = NonNullable<
	Awaited<ReturnType<typeof getRecentSessionData>>
>;
export type GetRecentSessionDataQueryError =
	| GetRecentSessionData400
	| void
	| GetRecentSessionData500;

export function useGetRecentSessionData<
	TData = Awaited<ReturnType<typeof getRecentSessionData>>,
	TError = GetRecentSessionData400 | void | GetRecentSessionData500,
>(
	options: {
		query: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getRecentSessionData>>,
				TError,
				TData
			>
		> &
			Pick<
				DefinedInitialDataOptions<
					Awaited<ReturnType<typeof getRecentSessionData>>,
					TError,
					Awaited<ReturnType<typeof getRecentSessionData>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetRecentSessionData<
	TData = Awaited<ReturnType<typeof getRecentSessionData>>,
	TError = GetRecentSessionData400 | void | GetRecentSessionData500,
>(
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getRecentSessionData>>,
				TError,
				TData
			>
		> &
			Pick<
				UndefinedInitialDataOptions<
					Awaited<ReturnType<typeof getRecentSessionData>>,
					TError,
					Awaited<ReturnType<typeof getRecentSessionData>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetRecentSessionData<
	TData = Awaited<ReturnType<typeof getRecentSessionData>>,
	TError = GetRecentSessionData400 | void | GetRecentSessionData500,
>(
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getRecentSessionData>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get the most recent sensor data for the current user's devices
 */

export function useGetRecentSessionData<
	TData = Awaited<ReturnType<typeof getRecentSessionData>>,
	TError = GetRecentSessionData400 | void | GetRecentSessionData500,
>(
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof getRecentSessionData>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
} {
	const queryOptions = getGetRecentSessionDataQueryOptions(options);

	const query = useQuery(queryOptions, queryClient) as UseQueryResult<
		TData,
		TError
	> & { queryKey: DataTag<QueryKey, TData, TError> };

	return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get the most recent sensor data for the current user's devices
 */
export const useSetGetRecentSessionDataQueryData = () => {
	const queryClient = useQueryClient();
	return (
		updater:
			| Awaited<ReturnType<typeof getRecentSessionData>>
			| undefined
			| ((
					old: Awaited<ReturnType<typeof getRecentSessionData>> | undefined,
			  ) => Awaited<ReturnType<typeof getRecentSessionData>> | undefined),
		$exactMatch: boolean = true,
	) => {
		queryClient.setQueriesData<
			Awaited<ReturnType<typeof getRecentSessionData>>
		>(
			{ exact: $exactMatch, queryKey: getGetRecentSessionDataQueryKey() },
			updater,
		);
	};
};

/**
 * @summary Get the most recent sensor data for the current user's devices
 */
export const useGetGetRecentSessionDataQueryData = () => {
	const queryClient = useQueryClient();
	return () =>
		queryClient.getQueryData<Awaited<ReturnType<typeof getRecentSessionData>>>(
			getGetRecentSessionDataQueryKey(),
		);
};

export const getLoadSessionDataUrl = (params?: LoadSessionDataParams) => {
	const normalizedParams = new URLSearchParams();

	Object.entries(params || {}).forEach(([key, value]) => {
		if (value !== undefined) {
			normalizedParams.append(key, value === null ? "null" : String(value));
		}
	});

	const stringifiedParams = normalizedParams.toString();

	return stringifiedParams.length > 0
		? `/session/data?${stringifiedParams}`
		: `/session/data`;
};

/**
 * **Access:** Requires a user session token.
 * @summary Load full session data for the current user (admin can target a specific userId)
 */
export const loadSessionData = async (
	params?: LoadSessionDataParams,
	options?: Parameters<typeof customFetch>[1],
): Promise<LoadSessionData200> => {
	return customFetch<LoadSessionData200>(getLoadSessionDataUrl(params), {
		...options,
		method: "GET",
	});
};

export const getLoadSessionDataQueryKey = (params?: LoadSessionDataParams) => {
	return ["session", "data", ...(params ? [params] : [])] as const;
};

export const getLoadSessionDataQueryOptions = <
	TData = Awaited<ReturnType<typeof loadSessionData>>,
	TError = LoadSessionData400 | void | LoadSessionData403 | LoadSessionData500,
>(
	params?: LoadSessionDataParams,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof loadSessionData>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey = queryOptions?.queryKey ?? getLoadSessionDataQueryKey(params);

	const queryFn: QueryFunction<Awaited<ReturnType<typeof loadSessionData>>> = ({
		signal,
	}) => loadSessionData(params, { signal, ...requestOptions });

	return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
		Awaited<ReturnType<typeof loadSessionData>>,
		TError,
		TData
	> & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type LoadSessionDataQueryResult = NonNullable<
	Awaited<ReturnType<typeof loadSessionData>>
>;
export type LoadSessionDataQueryError =
	| LoadSessionData400
	| void
	| LoadSessionData403
	| LoadSessionData500;

export function useLoadSessionData<
	TData = Awaited<ReturnType<typeof loadSessionData>>,
	TError = LoadSessionData400 | void | LoadSessionData403 | LoadSessionData500,
>(
	params: undefined | LoadSessionDataParams,
	options: {
		query: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof loadSessionData>>,
				TError,
				TData
			>
		> &
			Pick<
				DefinedInitialDataOptions<
					Awaited<ReturnType<typeof loadSessionData>>,
					TError,
					Awaited<ReturnType<typeof loadSessionData>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useLoadSessionData<
	TData = Awaited<ReturnType<typeof loadSessionData>>,
	TError = LoadSessionData400 | void | LoadSessionData403 | LoadSessionData500,
>(
	params?: LoadSessionDataParams,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof loadSessionData>>,
				TError,
				TData
			>
		> &
			Pick<
				UndefinedInitialDataOptions<
					Awaited<ReturnType<typeof loadSessionData>>,
					TError,
					Awaited<ReturnType<typeof loadSessionData>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useLoadSessionData<
	TData = Awaited<ReturnType<typeof loadSessionData>>,
	TError = LoadSessionData400 | void | LoadSessionData403 | LoadSessionData500,
>(
	params?: LoadSessionDataParams,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof loadSessionData>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Load full session data for the current user (admin can target a specific userId)
 */

export function useLoadSessionData<
	TData = Awaited<ReturnType<typeof loadSessionData>>,
	TError = LoadSessionData400 | void | LoadSessionData403 | LoadSessionData500,
>(
	params?: LoadSessionDataParams,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof loadSessionData>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
} {
	const queryOptions = getLoadSessionDataQueryOptions(params, options);

	const query = useQuery(queryOptions, queryClient) as UseQueryResult<
		TData,
		TError
	> & { queryKey: DataTag<QueryKey, TData, TError> };

	return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Load full session data for the current user (admin can target a specific userId)
 */
export const useSetLoadSessionDataQueryData = () => {
	const queryClient = useQueryClient();
	return (
		params: LoadSessionDataParams | undefined,
		updater:
			| Awaited<ReturnType<typeof loadSessionData>>
			| undefined
			| ((
					old: Awaited<ReturnType<typeof loadSessionData>> | undefined,
			  ) => Awaited<ReturnType<typeof loadSessionData>> | undefined),
		$exactMatch: boolean = true,
	) => {
		queryClient.setQueriesData<Awaited<ReturnType<typeof loadSessionData>>>(
			{ exact: $exactMatch, queryKey: getLoadSessionDataQueryKey(params) },
			updater,
		);
	};
};

/**
 * @summary Load full session data for the current user (admin can target a specific userId)
 */
export const useGetLoadSessionDataQueryData = () => {
	const queryClient = useQueryClient();
	return (params?: LoadSessionDataParams) =>
		queryClient.getQueryData<Awaited<ReturnType<typeof loadSessionData>>>(
			getLoadSessionDataQueryKey(params),
		);
};

export const getPollForUpdatesUrl = () => {
	return `/session/poll-for-updates`;
};

/**
 * **Access:** Requires a user session token.
 * @summary Poll for recently updated devices and recent sensor data for the current user
 */
export const pollForUpdates = async (
	pollForUpdatesBody?: PollForUpdatesBody,
	options?: Parameters<typeof customFetch>[1],
): Promise<PollForUpdates200> => {
	const getHeaders = (
		h?: NonNullable<RequestInit["headers"]>,
	): Record<string, string | readonly string[]> => {
		if (!h) return {};
		if (h instanceof Headers) return Object.fromEntries(h.entries());
		if (Symbol.iterator in h) {
			return Object.fromEntries(
				Array.from(
					h as Iterable<Iterable<string>>,
					(entry) => Array.from(entry) as [string, string],
				),
			);
		}
		const headers: Record<string, string | readonly string[]> = {};
		for (const [name, value] of Object.entries<
			string | readonly string[] | undefined
		>(h)) {
			if (value !== undefined) headers[name] = value;
		}
		return headers;
	};
	return customFetch<PollForUpdates200>(getPollForUpdatesUrl(), {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...getHeaders(options?.headers),
		},
		body: JSON.stringify(pollForUpdatesBody),
	});
};

export const getPollForUpdatesQueryKey = (
	pollForUpdatesBody?: PollForUpdatesBody,
) => {
	return ["POST", "session", "poll-for-updates", pollForUpdatesBody] as const;
};

export const getPollForUpdatesQueryOptions = <
	TData = Awaited<ReturnType<typeof pollForUpdates>>,
	TError = PollForUpdates400 | PollForUpdates401 | PollForUpdates500,
>(
	pollForUpdatesBody?: PollForUpdatesBody,
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof pollForUpdates>>, TError, TData>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey =
		queryOptions?.queryKey ?? getPollForUpdatesQueryKey(pollForUpdatesBody);

	const queryFn: QueryFunction<Awaited<ReturnType<typeof pollForUpdates>>> = ({
		signal,
	}) => pollForUpdates(pollForUpdatesBody, { signal, ...requestOptions });

	return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
		Awaited<ReturnType<typeof pollForUpdates>>,
		TError,
		TData
	> & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PollForUpdatesQueryResult = NonNullable<
	Awaited<ReturnType<typeof pollForUpdates>>
>;
export type PollForUpdatesQueryError =
	| PollForUpdates400
	| PollForUpdates401
	| PollForUpdates500;

export function usePollForUpdates<
	TData = Awaited<ReturnType<typeof pollForUpdates>>,
	TError = PollForUpdates400 | PollForUpdates401 | PollForUpdates500,
>(
	pollForUpdatesBody: undefined | PollForUpdatesBody,
	options: {
		query: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof pollForUpdates>>, TError, TData>
		> &
			Pick<
				DefinedInitialDataOptions<
					Awaited<ReturnType<typeof pollForUpdates>>,
					TError,
					Awaited<ReturnType<typeof pollForUpdates>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePollForUpdates<
	TData = Awaited<ReturnType<typeof pollForUpdates>>,
	TError = PollForUpdates400 | PollForUpdates401 | PollForUpdates500,
>(
	pollForUpdatesBody?: PollForUpdatesBody,
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof pollForUpdates>>, TError, TData>
		> &
			Pick<
				UndefinedInitialDataOptions<
					Awaited<ReturnType<typeof pollForUpdates>>,
					TError,
					Awaited<ReturnType<typeof pollForUpdates>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePollForUpdates<
	TData = Awaited<ReturnType<typeof pollForUpdates>>,
	TError = PollForUpdates400 | PollForUpdates401 | PollForUpdates500,
>(
	pollForUpdatesBody?: PollForUpdatesBody,
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof pollForUpdates>>, TError, TData>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Poll for recently updated devices and recent sensor data for the current user
 */

export function usePollForUpdates<
	TData = Awaited<ReturnType<typeof pollForUpdates>>,
	TError = PollForUpdates400 | PollForUpdates401 | PollForUpdates500,
>(
	pollForUpdatesBody?: PollForUpdatesBody,
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof pollForUpdates>>, TError, TData>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
} {
	const queryOptions = getPollForUpdatesQueryOptions(
		pollForUpdatesBody,
		options,
	);

	const query = useQuery(queryOptions, queryClient) as UseQueryResult<
		TData,
		TError
	> & { queryKey: DataTag<QueryKey, TData, TError> };

	return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Poll for recently updated devices and recent sensor data for the current user
 */
export const useSetPollForUpdatesQueryData = () => {
	const queryClient = useQueryClient();
	return (
		pollForUpdatesBody: PollForUpdatesBody | undefined,
		updater:
			| Awaited<ReturnType<typeof pollForUpdates>>
			| undefined
			| ((
					old: Awaited<ReturnType<typeof pollForUpdates>> | undefined,
			  ) => Awaited<ReturnType<typeof pollForUpdates>> | undefined),
		$exactMatch: boolean = true,
	) => {
		queryClient.setQueriesData<Awaited<ReturnType<typeof pollForUpdates>>>(
			{
				exact: $exactMatch,
				queryKey: getPollForUpdatesQueryKey(pollForUpdatesBody),
			},
			updater,
		);
	};
};

/**
 * @summary Poll for recently updated devices and recent sensor data for the current user
 */
export const useGetPollForUpdatesQueryData = () => {
	const queryClient = useQueryClient();
	return (pollForUpdatesBody?: PollForUpdatesBody) =>
		queryClient.getQueryData<Awaited<ReturnType<typeof pollForUpdates>>>(
			getPollForUpdatesQueryKey(pollForUpdatesBody),
		);
};
